import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import {
	Box,
	Typography,
	Grid,
	CircularProgress,
	Accordion,
	AccordionSummary,
	AccordionDetails,
	Dialog,
	DialogTitle,
	DialogContent,
	IconButton,
	Select,
	MenuItem,
	FormControl,
	InputLabel,
	SelectChangeEvent,
	Tooltip,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import { getAllCategories, getSingleCategorySubproducts } from '../../../../services/api/MenuAPI';
import Image from '../../Image';

interface Category {
	_id: string;
	name: string;
	description: string;
	categoryImage: string;
}

interface Subcategory {
	_id: string;
	subCategory: string;
	description: string;
	subCategoryImage: string;
}

const ROW_OPTIONS = [5, 10, 15, 20];

export default function MenuContainer1() {
	const [categories, setCategories] = useState<Category[]>([]);
	const [expanded, setExpanded] = useState<string[]>([]);
	const [loadingSub, setLoadingSub] = useState<boolean>(false);
	const [subcategoriesMap, setSubcategoriesMap] = useState<Record<string, Subcategory[]>>({});
	const [selectedSub, setSelectedSub] = useState<Subcategory | null>(null);
	const [openDialog, setOpenDialog] = useState<boolean>(false);

	const [rowsPerPage, setRowsPerPage] = useState<number>(10);
	const [visibleRows, setVisibleRows] = useState<number>(10);
	const [isFetchingMore, setIsFetchingMore] = useState<boolean>(false);

	const sentinelRef = useRef<HTMLDivElement | null>(null);
	const observerRef = useRef<IntersectionObserver | null>(null);

	useEffect(() => {
		const fetchAllCategories = async () => {
			try {
				const response = await getAllCategories();
				const cats: Category[] = response?.data?.categories || [];
				setCategories(cats);
			} catch (error) {
				console.error('Error fetching categories', error);
			}
		};
		fetchAllCategories();
	}, []);

	const hasMore = visibleRows < categories.length;

	const loadMore = useCallback(() => {
		if (isFetchingMore || !hasMore) return;
		setIsFetchingMore(true);
		setTimeout(() => {
			setVisibleRows((prev) => Math.min(prev + rowsPerPage, categories.length));
			setIsFetchingMore(false);
		}, 400);
	}, [isFetchingMore, hasMore, rowsPerPage, categories.length]);

	useEffect(() => {
		if (observerRef.current) observerRef.current.disconnect();

		observerRef.current = new IntersectionObserver(
			(entries) => {
				if (entries[0].isIntersecting) loadMore();
			},
			{ threshold: 0.1 }
		);

		if (sentinelRef.current) observerRef.current.observe(sentinelRef.current);

		return () => observerRef.current?.disconnect();
	}, [loadMore]);

	const handleAccordionChange = useCallback(
		(categoryName: string) => async (_: React.SyntheticEvent, isExpanded: boolean) => {
			setExpanded((prev) =>
				isExpanded ? [...prev, categoryName] : prev.filter((name) => name !== categoryName)
			);

			if (isExpanded && !subcategoriesMap[categoryName]) {
				setLoadingSub(true);
				try {
					const response = await getSingleCategorySubproducts(categoryName);
					const subs: Subcategory[] = response?.data?.subcategories || [];
					setSubcategoriesMap((prev) => ({ ...prev, [categoryName]: subs }));
				} catch (error) {
					console.error('Error fetching subcategories', error);
				} finally {
					setLoadingSub(false);
				}
			}
		},
		[subcategoriesMap]
	);

	const handleCardClick = (sub: Subcategory) => {
		setSelectedSub(sub);
		setOpenDialog(true);
	};

	const handleCloseDialog = () => {
		setOpenDialog(false);
		setSelectedSub(null);
	};

	const handleRowsPerPageChange = (event: SelectChangeEvent<number>) => {
		const value = event.target.value as number;
		setRowsPerPage(value);
		setVisibleRows(value);
	};

	const displayedCategories = useMemo(
		() => categories.slice(0, visibleRows),
		[categories, visibleRows]
	);

	return (
		<Box padding={2}>
			<Box textAlign="center" padding={3}>
				<Typography variant="h6" gutterBottom>
					Explore our thoughtfully crafted wedding menu selections designed to impress
					every guest. While each package offers a complete dining experience, we&apos;re
					happy to tailor dishes to suit your unique tastes and traditions.
				</Typography>
			</Box>

			<Grid container justifyContent="center" padding={3}>
				<Grid size={{ xs: 12, md: 10, lg: 10, xl: 12 }}>
					{categories.length > 5 && (
						<Box display="flex" justifyContent="flex-end" alignItems="center" mb={2}>
							<FormControl size="small" sx={{ minWidth: 140 }}>
								<InputLabel>Rows Per Page</InputLabel>
								<Select
									value={rowsPerPage}
									label="Rows Per Page"
									onChange={handleRowsPerPageChange}
								>
									{ROW_OPTIONS.map((option) => (
										<MenuItem key={option} value={option}>
											{option}
										</MenuItem>
									))}
								</Select>
							</FormControl>
						</Box>
					)}

					<Grid container spacing={2}>
						{/* Render one ordered list at every breakpoint. MUI wraps items
						    into two columns on desktop and one column on mobile. */}
						{displayedCategories.map((category) => (
							<Grid key={category._id} size={{ xs: 12, md: 6 }}>
								<Box key={category._id} mb={2}>
									<Accordion
										expanded={expanded.includes(category.name)}
										onChange={handleAccordionChange(category.name)}
										sx={{ borderRadius: 2 }}
									>
										<AccordionSummary expandIcon={<ExpandMoreIcon />}>
											<Box display="flex" alignItems="center">
												<Image
													src={category.categoryImage}
													alt={category.name}
													style={{
														width: 56,
														height: 56,
														marginRight: 16,
														borderRadius: 8,
														objectFit: 'cover',
													}}
												/>
												<Box>
													<Typography
														variant="subtitle1"
														fontWeight="bold"
													>
														{category.name}
													</Typography>
													<Typography
														variant="body2"
														color="textSecondary"
														sx={{
															display: '-webkit-box',
															WebkitLineClamp: 2,
															WebkitBoxOrient: 'vertical',
															overflow: 'hidden',
														}}
													>
														{category.description}
													</Typography>
												</Box>
											</Box>
										</AccordionSummary>

										<AccordionDetails className="scrollable-details">
											{loadingSub && expanded.includes(category.name) ? (
												<Box display="flex" justifyContent="center" my={2}>
													<CircularProgress />
												</Box>
											) : subcategoriesMap[category.name]?.length > 0 ? (
												<Grid container spacing={2}>
													{subcategoriesMap[category.name].map((sub) => (
														<Grid
															size={{
																xs: 6,
																sm: 4,
																md: 2,
															}}
															key={sub._id}
														>
															<Box
																onClick={() => handleCardClick(sub)}
																sx={{
																	cursor: 'pointer',
																	backgroundColor: '#fff',
																	borderRadius: 2,
																	boxShadow: 2,
																	overflow: 'hidden',
																	transition: 'transform 0.3s',
																	'&:hover': {
																		transform: 'scale(1.02)',
																		boxShadow: 4,
																	},
																}}
															>
																<Image
																	src={sub.subCategoryImage}
																	alt={sub.subCategory}
																	zoomOnHover
																	style={{
																		width: '100%',
																		height: 140,
																		objectFit: 'cover',
																	}}
																/>
																<Box p={1}>
																	<Tooltip
																		title={sub.subCategory}
																		followCursor
																	>
																		<Typography
																			variant="subtitle2"
																			fontWeight="bold"
																			sx={{
																				overflow: 'hidden',
																				textOverflow:
																					'ellipsis',
																				whiteSpace:
																					'nowrap',
																			}}
																		>
																			{sub.subCategory}
																		</Typography>
																	</Tooltip>
																</Box>
															</Box>
														</Grid>
													))}
												</Grid>
											) : (
												<Typography color="gray">
													Data is being updated...
												</Typography>
											)}
										</AccordionDetails>
									</Accordion>
								</Box>
							</Grid>
						))}
					</Grid>

					{hasMore && <Box ref={sentinelRef} sx={{ height: 1 }} />}

					{isFetchingMore && (
						<Box display="flex" justifyContent="center" mt={2} mb={1}>
							<CircularProgress size={28} color="error" />
						</Box>
					)}
				</Grid>
			</Grid>

			<Dialog open={openDialog} onClose={handleCloseDialog} fullWidth maxWidth="sm">
				<DialogTitle>
					{selectedSub?.subCategory}
					<IconButton
						aria-label="close"
						onClick={handleCloseDialog}
						sx={{ position: 'absolute', right: 8, top: 8 }}
					>
						<CloseIcon />
					</IconButton>
				</DialogTitle>
				<DialogContent dividers>
					{selectedSub && (
						<Box display="flex" flexDirection="column" alignItems="center">
							<Image
								src={selectedSub.subCategoryImage}
								alt={selectedSub.subCategory}
								style={{
									width: '100%',
									maxHeight: 250,
									objectFit: 'contain',
									marginBottom: 16,
								}}
							/>
							<Typography variant="h4">{selectedSub.subCategory}</Typography>
							<Typography variant="body2" color="textSecondary" mt={1}>
								{selectedSub.description}
							</Typography>
						</Box>
					)}
				</DialogContent>
			</Dialog>
		</Box>
	);
}
